import { useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useBodyScrollLock } from "@/shared/lib/hooks/useBodyScrollLock"
import { findActiveItem, type CabinetId, type CabinetNavGroup } from "./cabinetNav"

const SidebarNav = ({ current, groups }: { current: CabinetId; groups: CabinetNavGroup[] }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const active = findActiveItem(groups, pathname)
  const title = t(`workspaces.${current}`)

  return (
    <nav aria-label={title} className="flex flex-col gap-5 px-3 py-5">
      <p className="cabinet-script px-3 text-xl leading-tight text-[var(--ws-accent)]">{title}</p>
      {groups.map((group) => (
        <div key={group.id} className={`flex flex-col gap-0.5 ${group.separated ? "mt-2 border-t border-lib-border pt-4" : ""}`}>
          {group.titleKey ? (
            <p className="px-3 pb-1 text-[11px] uppercase tracking-wider text-lib-text-muted">{t(group.titleKey)}</p>
          ) : null}
          {group.items.map((item) => {
            const isActive = item === active
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActive ? "page" : undefined}
                className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "cabinet-nav-active" : "text-lib-text-secondary hover:bg-lib-bg hover:text-lib-text"
                }`}
              >
                <i className={`${item.icon} text-base shrink-0`} aria-hidden />
                <span className="truncate">{t(item.labelKey)}</span>
              </Link>
            )
          })}
        </div>
      ))}
    </nav>
  )
}

export const CabinetSidebar = ({
  current,
  groups,
  mobileOpen,
  onClose,
}: {
  current: CabinetId
  groups: CabinetNavGroup[]
  mobileOpen: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const closeRef = useRef<HTMLButtonElement | null>(null)
  useBodyScrollLock(mobileOpen)

  useEffect(() => {
    if (!mobileOpen) return
    closeRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [mobileOpen, onClose])

  return (
    <>
      <aside className="cabinet-sidebar hidden lg:block w-60 shrink-0 border-r border-lib-border sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
        <SidebarNav current={current} groups={groups} />
      </aside>
      {mobileOpen ? (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t(`workspaces.${current}`)}>
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            aria-label={t("workspaces.closeSidebar")}
            tabIndex={-1}
          />
          <div
            id="cabinet-sidebar"
            className="cabinet-sidebar workspace-shell absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] shadow-xl overflow-y-auto"
            data-workspace={current}
          >
            <div className="flex justify-end px-3 pt-3">
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-md text-lib-text hover:bg-lib-bg"
                aria-label={t("workspaces.closeSidebar")}
              >
                <i className="ri-close-line text-xl" aria-hidden />
              </button>
            </div>
            <SidebarNav current={current} groups={groups} />
          </div>
        </div>
      ) : null}
    </>
  )
}
